import React, { useState } from 'react';
import { ExportIcon, XMarkIcon, SpinnerIcon } from '../../../components/Icons';
import { escapeCsvField } from '../../../utils/csvUtils';
import { Property } from '../../../types';

const EXPORT_COLUMNS = [
    { key: 'codigo', label: 'Código' },
    { key: 'status', label: 'Estado' },
    { key: 'codigo_actual', label: 'Nuevo Código' },
    { key: 'owner', label: 'Owner' },
    { key: 'codigo_brp', label: 'Código BRP' },
    { key: 'precio_venta', label: 'Precio Venta' },
    { key: 'direccion', label: 'Dirección' },
    { key: 'ciudad', label: 'Ciudad' },
    { key: 'localidad', label: 'Localidad' },
    { key: 'neighborhoodName', label: 'Nombre Barrio' },
    { key: 'numero_matricula', label: 'No. Matrícula' },
    { key: 'valor_administracion', label: 'Administración' },
    { key: 'disponibilidad_visitas', label: 'Horarios Visita' },
    { key: 'nombre_cliente', label: 'Cliente' },
    { key: 'telefono_cliente', label: 'Teléfono Cliente' },
];

interface OperationExportModalProps {
    isOpen: boolean;
    onClose: () => void;
    onFetchAll: () => Promise<Property[]>;
}

export const OperationExportModal: React.FC<OperationExportModalProps> = ({ isOpen, onClose, onFetchAll }) => {
    const [selectedColumns, setSelectedColumns] = useState<string[]>(EXPORT_COLUMNS.map(c => c.key));
    const [isExporting, setIsExporting] = useState(false);

    if (!isOpen) return null;

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const allFiltered = await onFetchAll();
            const headers = EXPORT_COLUMNS.filter(col => selectedColumns.includes(col.key)).map(col => col.label);
            const rows = allFiltered.map(p => {
                return EXPORT_COLUMNS.filter(col => selectedColumns.includes(col.key)).map(col => {
                    const val = (p as any)[col.key];
                    return escapeCsvField(val ?? "");
                });
            });
            const csvContent = ["\uFEFF", headers.join(','), ...rows.map(row => row.join(','))].join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `Export_Operaciones_183150_${new Date().getTime()}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            onClose();
        } catch (e) {
            alert("Error al exportar.");
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-900/90 backdrop-blur-md z-[200] flex items-center justify-center p-4">
            <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg overflow-hidden animate-fade-in-up">
                <header className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <div className="flex items-center gap-3">
                        <ExportIcon className="w-6 h-6 text-sky-600" />
                        <h2 className="text-xl font-black uppercase text-gray-800">Exportar CSV</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="w-6 h-6"/></button>
                </header>
                <div className="p-8">
                    <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto pr-2 custom-scrollbar border-y border-gray-100 py-4">
                        {EXPORT_COLUMNS.map(col => (
                            <label key={col.key} className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${selectedColumns.includes(col.key) ? 'bg-sky-50 border-sky-200 text-sky-900' : 'border-gray-50 text-gray-500'}`}>
                                <input type="checkbox" checked={selectedColumns.includes(col.key)} onChange={(e) => e.target.checked ? setSelectedColumns([...selectedColumns, col.key]) : setSelectedColumns(selectedColumns.filter(c => c !== col.key))} className="w-4 h-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500" />
                                <span className="text-xs font-bold uppercase">{col.label}</span>
                            </label>
                        ))}
                    </div>
                </div>
                <footer className="px-8 py-6 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                    <button onClick={onClose} className="px-6 py-2.5 text-xs font-black text-gray-400" disabled={isExporting}>Cancelar</button>
                    <button onClick={handleExport} disabled={isExporting} className="px-8 py-2.5 bg-gray-900 text-white rounded-xl text-xs font-black uppercase flex items-center gap-2">
                        {isExporting ? <SpinnerIcon className="w-4 h-4" /> : <ExportIcon className="w-4 h-4" />}
                        {isExporting ? 'Procesando...' : 'Descargar'}
                    </button>
                </footer>
            </div>
        </div>
    );
};